import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { HiArrowLeft, HiEye, HiEyeSlash } from "react-icons/hi2";
import { useUserPrivacy } from "../../stores";
import Header from "../../components/layout/Header";
import LoaderBackdrop from "../../components/LoaderBackdrop";
import Toast from "../../components/Toast";

type ToastType = "success" | "error" | "warning" | "info";

const PrivacySettings: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  // Use privacy hook from store
  const { isPrivate, updatePrivacy, isLoading: storeLoading } = useUserPrivacy(currentUser?.uid || null);

  const [toast, setToast] = useState<{
    open: boolean;
    type: ToastType;
    message: string;
  }>({
    open: false,
    type: "success",
    message: "",
  });

  const showToast = (type: ToastType, message: string) => {
    setToast({ open: true, type, message });
    setTimeout(() => setToast(prev => ({ ...prev, open: false })), 3000);
  };

  const handlePrivacyToggle = async () => {
    if (!currentUser?.uid) return;

    try {
      setLoading(true);
      const newPrivacyValue = !isPrivate;
      
      await updatePrivacy(newPrivacyValue);
      
      showToast(
        "success",
        newPrivacyValue 
          ? "🔒 Profile is now private. New followers will need your approval."
          : "🔓 Profile is now public. Anyone can follow you."
      );
    } catch (error) {
      console.error("Error updating privacy setting:", error);
      showToast("error", "Failed to update privacy setting. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (storeLoading && isPrivate === null) {
    return (
      <div className="w-full pt-16">
        <Header />
        <div className="container max-w-2xl mx-auto py-8 px-4">
          <div className="flex justify-center items-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-base-content"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full pt-16">
      <Header />
      <LoaderBackdrop open={loading} />
      
      <Toast
        open={toast.open}
        type={toast.type}
        message={toast.message}
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
      />

      <div className="container max-w-2xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="flex items-center mb-8">
          <button
            onClick={() => navigate("/settings")}
            className="mr-4 p-2 text-base-content/50 hover:text-base-content/70 hover:bg-base-200 rounded-full transition-all"
          >
            <HiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-base-content">Privacy Settings</h1>
            <p className="text-base-content/70 text-sm mt-1">Control who can see your profile and follow you</p>
          </div>
        </div>

        {/* Privacy Toggle Card */}
        <div className="bg-base-100 rounded-lg shadow-sm border border-base-300 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 mt-1">
                {isPrivate ? (
                  <HiEyeSlash className="w-6 h-6 text-base-content/80" />
                ) : (
                  <HiEye className="w-6 h-6 text-base-content/50" />
                )}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-base-content mb-1">
                  Private Profile
                </h3>
                <p className="text-sm text-base-content/70">
                  {isPrivate 
                    ? "Only your current followers can see your posts and profile" 
                    : "Anyone can see your profile and follow you"
                  }
                </p>
              </div>
            </div>
            
            {/* Toggle Switch */}
            <div className="flex-shrink-0 ml-4">
              <button
                onClick={handlePrivacyToggle}
                disabled={loading}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-base-content/60 focus:ring-offset-2 disabled:opacity-50 ${
                  isPrivate ? 'bg-neutral' : 'bg-base-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-base-100 transition-transform ${
                    isPrivate ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Simple Note */}
        {isPrivate && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              <strong>Note:</strong> New users won't be able to follow you. Your existing followers can still see your content.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrivacySettings;